/**
 * Document scroll lock helpers for app-beta-document-scroll pages.
 * Capture scrollY before body position:fixed; restore on unlock.
 * Never call window.scrollTo(0, 0) as a side effect of open/close.
 *
 * Important: html/body.app-beta-document-scroll use `overflow-y: auto !important`,
 * so overflow locks must setProperty(..., 'important') or they do nothing.
 *
 * Upgrade CTAs may wait on an async billing gate before the modal mounts —
 * rememberScrollYForNextLock() captures Y synchronously on tap so a late
 * lock does not freeze at y=0 after iOS has already jumped.
 */

let lockedY = 0
let lockCount = 0
/** Consumed by the next lockDocumentScroll(); cleared on unlock or cancel. */
let pendingLockY: number | null = null

export function getDocumentScrollY(): number {
  if (typeof window === 'undefined') return 0
  return (
    window.scrollY ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0
  )
}

/** Capture scrollY now for the next modal lock (survives async billing gate). */
export function rememberScrollYForNextLock(y?: number): void {
  if (typeof window === 'undefined') return
  pendingLockY = typeof y === 'number' && Number.isFinite(y) ? y : getDocumentScrollY()
}

export function clearRememberedScrollYForNextLock(): void {
  pendingLockY = null
}

export function peekRememberedScrollYForNextLock(): number | null {
  return pendingLockY
}

export function restoreDocumentScrollY(y: number): void {
  if (typeof window === 'undefined') return
  const target = Math.max(0, Math.round(y))
  const apply = () => {
    window.scrollTo(0, target)
    document.documentElement.scrollTop = target
    document.body.scrollTop = target
  }
  apply()
  requestAnimationFrame(apply)
}

export function lockDocumentScroll(explicitY?: number): number {
  if (typeof window === 'undefined' || typeof document === 'undefined') return 0
  lockCount += 1
  if (lockCount > 1) return lockedY

  const fromPending = pendingLockY
  pendingLockY = null
  lockedY =
    typeof explicitY === 'number' && Number.isFinite(explicitY)
      ? explicitY
      : fromPending != null
        ? fromPending
        : getDocumentScrollY()

  const html = document.documentElement
  const body = document.body
  body.style.position = 'fixed'
  body.style.top = `-${lockedY}px`
  body.style.left = '0'
  body.style.right = '0'
  body.style.width = '100%'
  // Beat app-beta-document-scroll `overflow-y: auto !important`.
  html.style.setProperty('overflow', 'hidden', 'important')
  body.style.setProperty('overflow', 'hidden', 'important')
  return lockedY
}

export function unlockDocumentScroll(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  lockCount = Math.max(0, lockCount - 1)
  if (lockCount > 0) return

  const html = document.documentElement
  const body = document.body
  const y = lockedY
  body.style.position = ''
  body.style.top = ''
  body.style.left = ''
  body.style.right = ''
  body.style.width = ''
  body.style.paddingRight = ''
  html.style.removeProperty('overflow')
  body.style.removeProperty('overflow')
  restoreDocumentScrollY(y)
  lockedY = 0
  pendingLockY = null
}

/** Test/helper — reset lock bookkeeping without touching the DOM. */
export function __resetDocumentScrollLockForTests(): void {
  lockCount = 0
  lockedY = 0
  pendingLockY = null
}
